import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from '@core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { UserManagementService } from '@services';
import { NotificationsService } from '@ui';
import { map, of, tap } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { ClientManagementActions } from '../clients-managements';
import { ClientUsersActions } from './client-users.actions';

@Injectable()
export class ClientUsersEffects {
  private userManagementService = inject(UserManagementService);
  private actions$ = inject(Actions);
  private router = inject(Router);
  private appService = inject(AppService);
  private notificationService = inject(NotificationsService);

  clientIdChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClientManagementActions.clientIdChanged),
      switchMap(({ clientId }) => [
        ClientUsersActions.resetClientUsers(),
        ClientUsersActions.loadClientUsers({ clientId }),
      ]),
    );
  });

  loadUserList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientUsersActions.loadClientUsers),
      switchMap(action =>
        this.userManagementService
          .getClientUsersList(action.clientId)
          .pipe(map(clientUsers => ClientUsersActions.clientUsersLoaded({ clientUsersList: clientUsers }))),
      ),
    ),
  );

  addNewClientUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientUsersActions.addClientUser),
      switchMap(action =>
        this.userManagementService.addClientUser(action.clientId, action.newUserData).pipe(
          map(newUser => ClientUsersActions.clientUserCreated({ createdUser: newUser })),
          catchError(error => of(ClientUsersActions.clientUserError({ error }))),
        ),
      ),
    ),
  );

  newClientCreated = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientUsersActions.clientUserCreated),
        tap(async () => {
          await this.router.navigateByUrl('/user-management');
          this.notificationService.showSuccessMessage('User was successfully created');
        }),
      ),
    { dispatch: false },
  );

  userClientErrorHandler = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientUsersActions.clientUserError),
        map(action => {
          this.notificationService.showErrorMessage(action.error.message);
        }),
      ),
    { dispatch: false },
  );

  updateUserClient = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClientUsersActions.updateUserClient),
      switchMap(action =>
        this.userManagementService
          .updateUserData(this.appService.currentClient, action.clientUserId, action.clientUser)
          .pipe(map(updatedUser => ClientUsersActions.updateUserClientSuccess({ clientUser: updatedUser }))),
      ),
    );
  });

  updateUserClientSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientUsersActions.updateUserClientSuccess),
        tap(user =>
          this.notificationService.showSuccessMessage(
            `${user.clientUser.firstName} ${user.clientUser.lastName} is successfully updated`,
          ),
        ),
      ),
    { dispatch: false },
  );

  resendUserInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientUsersActions.resendUserInvitation),
      switchMap(action =>
        this.userManagementService.resendInvitation(this.appService.currentClient, action.clientUser.id).pipe(
          map(() => {
            this.notificationService.showSuccessMessage(
              `${action.clientUser.firstName} ${action.clientUser.lastName} has been resent an invitation to the mail`,
            );

            return ClientUsersActions.resendUserInvitationSuccess();
          }),
          catchError(() => {
            this.notificationService.showErrorMessage('Resend Invitation failed');

            return of(ClientUsersActions.resendUserInvitationError());
          }),
        ),
      ),
    ),
  );

  resetTotp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientUsersActions.resetTotp),
      switchMap(action =>
        this.userManagementService.resetTotp(this.appService.currentClient, action.clientUser.id).pipe(
          map(() => {
            this.notificationService.showSuccessMessage(
              `2FA has been reset for ${action.clientUser.firstName} ${action.clientUser.lastName}`,
            );

            return ClientUsersActions.resetTotpSuccess({
              clientUser: action.clientUser,
              totpGracePeriodEndsAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            });
          }),
          catchError(() => {
            this.notificationService.showErrorMessage('Reset 2FA failed');

            return of(ClientUsersActions.resetTotpError());
          }),
        ),
      ),
    ),
  );
}
